import { Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/client";
import { loginPathAfterSessionLoss } from "@/modules/auth/storeDeactivated";
import { useTranslate } from "@/lib/i18n";
import { queryKeys } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { useStoreBinding } from "@/modules/auth/useStoreBinding";
import { useLogout } from "@/modules/auth/useLogout";
import { isDriverOnly } from "@/modules/auth/landing";
import { DriverOnlyGate } from "@/modules/auth/DriverAppLandingPage";
import { Button, ErrorState, LoadingSkeleton, SectionCard } from "@a2z/ui";
import { Screen } from "@/shared/ui";
import { SplashScreen } from "@/shared/systemScreens/SplashScreen";
import { useSplashGate } from "@/shared/systemScreens/useSplashGate";
import { RealtimeNotice } from "@/modules/notifications/RealtimeNotice";
import { PrintCoordinator } from "@/modules/printing/PrintCoordinator";
import { RestaurantNotificationsProvider } from "@/modules/notifications/NotificationsProvider";
import { RestaurantNotificationsRuntime } from "@/modules/notifications/NotificationsRuntime";
import { CashierRequestAlerts } from "@/modules/pos/components/CashierRequestAlerts";
import { MenuAvailabilityAlerts } from "@/modules/pos/components/MenuAvailabilityAlerts";
import { ServiceRequestAlerts } from "@/modules/pos/serviceRequests/ServiceRequestAlerts";

/**
 * The gate for every authenticated route, and P5.3b's recovery.
 *
 * The problem it solves: seller login binds `stores[0]`
 * (`seller-auth.service.ts`), so an owner holding both a `DEFAULT` and a
 * `RESTAURANT` store lands on the wrong one and `RestaurantAuthGuard` answers
 * `RESTAURANT_STORE_REQUIRED`. That is not the user's mistake, so a bare 403 —
 * or worse a blank screen — would be the app failing them.
 *
 * Instead: read the account's stores, and offer the ones that are actually
 * reachable. If the account has no restaurant at all, say so plainly rather
 * than looping.
 *
 * The 403 is detected from the SESSION here rather than from a failed
 * restaurant call, so the recovery appears before any screen has a chance to
 * render an error.
 */
export function ProtectedLayout() {
  const t = useTranslate();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const { logout, isLoggingOut } = useLogout();
  // The binding and its one-shot repair (P5.3b) live in a hook because
  // `LandingRedirect` has to know the same thing — see `useStoreBinding`.
  const {
    session: data,
    isLoading,
    error,
    restaurantStores,
    isPending: isAutoSwitching,
    switchError,
    switchingTo,
    isSwitching,
    switchTo,
  } = useStoreBinding();

  // The boot screen. It paints only if the session is still unresolved after a
  // short beat, and holds a minimum time once painted — a fast boot never flashes.
  const splash = useSplashGate(isLoading);
  if (splash.show) return <SplashScreen visible={splash.visible} />;

  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    // A store an admin deactivated says so on the login page (which also
    // signs the session out), never "session expired".
    navigate(loginPathAfterSessionLoss(locale, error), { replace: true });
    return null;
  }

  if (!data) {
    // The session could not be loaded — in practice a dropped connection. It
    // gets a real recovery rather than a bare red line: this is the first
    // screen a waiter sees when the wifi goes, and "something went wrong" with
    // no button is a dead end on a device with no back affordance.
    return (
      <Screen title={t((m) => m.common.appName)}>
        <ErrorState
          message={errorMessage(error)}
          retryLabel={t((m) => m.common.retry)}
          onRetry={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.session })
          }
        />
      </Screen>
    );
  }

  if (data.mustChangePassword) {
    return <Navigate to={`/${locale}/change-password`} replace />;
  }

  // The bound store is not a restaurant — offer the ones that are.
  if (data.seller.storeKind !== "RESTAURANT") {
    // The single-restaurant auto-switch is in flight (or about to fire):
    // render the calm loading shell, not a one-option picker that blinks away.
    if (isAutoSwitching) {
      return (
        <Screen title={t((m) => m.common.appName)}>
          <div aria-busy="true" aria-live="polite" aria-label={t((m) => m.auth.switching)}>
            <LoadingSkeleton rows={3} />
          </div>
        </Screen>
      );
    }
    // Only RESTAURANT stores. Offering the rest sent the owner in a loop:
    // switching to a DEFAULT store landed straight back on this screen, with
    // logout as the only way out. The copy says "pick the restaurant you want
    // to work on", so the list has to actually be restaurants.
    const others = restaurantStores;

    // No other store to try: an explanation, not a spinner or a loop.
    if (others.length === 0) {
      return (
        <Screen title={t((m) => m.auth.noRestaurantTitle)}>
          <div className="flex flex-col gap-3">
            <SectionCard variant="flat" title={t((m) => m.auth.noRestaurantTitle)}>
              <p className="text-sm">{t((m) => m.auth.noRestaurantBody)}</p>
            </SectionCard>
            {/* The one screen that had NO way out: an account with no
                restaurant landed here with no logout — clearing cookies was
                the only escape. The likeliest visitor is someone who signed
                into another account from the SELLER portal (both apps share
                one session), and their fix is exactly this button. */}
            <Button
              variant="primary"
              size="touch"
              fullWidth
              isLoading={isLoggingOut}
              disabled={isLoggingOut}
              onClick={logout}
              data-testid="no-restaurant-logout"
            >
              {t((m) => m.common.logout)}
            </Button>
          </div>
        </Screen>
      );
    }

    return (
      <Screen title={t((m) => m.auth.wrongStoreTitle)}>
        <div className="flex flex-col gap-3">
          <p className="text-sm">{t((m) => m.auth.wrongStoreBody)}</p>
          {others.map((store) => (
            <Button
              key={store.storeId}
              variant="outline"
              size="touch"
              fullWidth
              // The tapped store shows the spinner; the rest just disable, so
              // it stays obvious which one is being switched to.
              isLoading={isSwitching && switchingTo === store.storeSlug}
              disabled={isSwitching}
              onClick={() => void switchTo(store.storeSlug)}
              data-testid={`switch-store-${store.storeSlug}`}
            >
              {store.storeName}
            </Button>
          ))}
          <p aria-live="polite" className="sr-only">
            {isSwitching ? t((m) => m.auth.switching) : ""}
          </p>
          {switchError ? (
            <p role="alert" className="text-sm text-[color:var(--danger)]">
              {switchError}
            </p>
          ) : null}
          <Button
            variant="primary"
            size="touch"
            fullWidth
            isLoading={isLoggingOut}
            disabled={isLoggingOut}
            onClick={logout}
          >
            {t((m) => m.common.logout)}
          </Button>
        </div>
      </Screen>
    );
  }

  // Driver backend B1: a driver-only account works in the Trioty driver app.
  // Every protected route answers it with that screen — before the till's
  // providers, alerts and pollers mount, none of which it may read.
  if (isDriverOnly(data.seller.permissions)) {
    return <DriverOnlyGate />;
  }

  // The till's bottom tab bar is GONE, and with it the two route checks that
  // decided where to hide it. It held exactly two links — the floor and the
  // back office — and both now have a better home: the floor is the screen you
  // are already on, and the back office is the X in the floor's own top bar.
  // A permanent bar to reach the screen you are looking at is chrome earning
  // nothing, and it was painting over the invoice's settle bar besides.
  //
  // Sign-out is unaffected: it never lived here. It is in the back office's
  // sidebar and drawer, which the floor's X reaches.

  return (
    <RestaurantNotificationsProvider
      sellerId={data.seller.id}
      storeId={data.seller.storeId}
      firebaseToken={data.firebaseToken}
    >
      <RealtimeNotice />
      <RestaurantNotificationsRuntime />
      <CashierRequestAlerts />
      <MenuAvailabilityAlerts />
      <ServiceRequestAlerts />
      <PrintCoordinator>
        <Outlet />
      </PrintCoordinator>
    </RestaurantNotificationsProvider>
  );
}
